import React from 'react';
import styled from 'styled-components';
import {
    FileText,
    Image as ImageIcon,
    Copy,
    ExternalLink,
    ZoomIn,
    ZoomOut,
} from 'lucide-react';
import type { AdjacentDocumentContext, DocumentBoundaryInfo, DocumentWarning, ReorderStatus } from '../../types';
import type { DuplicatePairItem } from '../DuplicatesModal';
import { PageMetaStrip } from './PageMetaStrip';
import { ContinuousPageStack } from './ContinuousPageStack';
import { renderMarkdownWithHighlight } from '../../utils/markdown';
import { selectPageImageWidth, withPageImageWidth } from '../../utils/pageImages';

export type ViewerMode = 'image' | 'ocr' | 'split' | 'duplicates';

// Re-exported from ActionsMenu so existing imports keep working during the
// transition. New code should import from ./ActionsMenu directly.
export type {
    PageActionItem,
    PageActionSeparator,
    PageActionEntry,
    PageActionsConfig,
} from './ActionsMenu';

export interface CanvasPage {
    id: string;
    label?: string;
    imageUrl?: string;
    pageNumber: number;
    /** Stored raster pixel dims; the scroll stack reserves slots from these. */
    width?: number | null;
    height?: number | null;
    warnings?: DocumentWarning[];
    notes?: string[];
    sourcePdf?: string | null;
    sourcePageIndex?: number | null;
    boundaryAfter?: DocumentBoundaryInfo | null;
    notesCount?: number;
    reorderStatus?: ReorderStatus | null;
    printedPageNumber?: number | null;
    internalDuplicate?: boolean;
    externalDuplicate?: boolean;
}

export interface DocumentCanvasPanelProps {
    /** The current page (viewport-derived in continuous mode) — drives the
     *  meta strip, OCR pane, and duplicates pane. */
    page: CanvasPage | null;
    /** Full ordered page set (filtered to match the rail). When provided,
     *  the image pane renders as a continuous scroll of all pages; when
     *  absent the legacy single-page pane is used (stories, move view). */
    pages?: CanvasPage[];
    /** Navigation target for the continuous stack. */
    activePageId?: string | null;
    /** Increments on every explicit nav so the stack re-scrolls even when the
     *  target page is unchanged. */
    scrollToken?: number;
    /** Identity of the document being shown; passed through to the continuous
     *  stack, which snaps to the top of the new document when it changes. */
    scrollResetKey?: string | number | null;
    /** Reports the page the viewport is currently on. */
    onViewportPageChange?: (pageId: string) => void;
    /** Controlled zoom (the inspect toolbar owns the control). When omitted,
     *  the panel falls back to its own zoom state + floating control (legacy
     *  single-page usage: stories, move view). */
    zoom?: number;
    ocrText?: string | null;
    searchQuery?: string;
    /** Controlled by the parent (lives in the toolbar). */
    mode: ViewerMode;
    duplicateData?: DuplicatePairItem[] | null;
    onNavigateToDuplicate?: (docId: string, pageNumber?: number) => void;
    getThumbnailUrl?: (pageId: string, pageIndex?: number) => string | null;
    /** Adjacent-document context shown at the ends of the continuous scroll. */
    previousDocument?: AdjacentDocumentContext | null;
    nextDocument?: AdjacentDocumentContext | null;
    onNavigateToDocument?: (docId: string) => void;
}

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    /* Fill the canvas wrapper's height so the image pane gets the extra space. */
    flex: 1 1 auto;
`;

// How far the split divider can be dragged (fraction of width given to the
// image pane) — keeps both panes usable at the extremes.
const SPLIT_MIN = 0.2;
const SPLIT_MAX = 0.8;

const Content = styled.div<{ $mode: ViewerMode; $splitRatio: number }>`
    display: grid;
    /* minmax(0, …) lets each column shrink below its content's intrinsic width
       so an oversized/zoomed image scrolls inside its pane instead of widening
       (and overflowing) the whole canvas. In split mode a 12px resizer column
       sits between the panes and the fr weights follow the drag ratio. */
    grid-template-columns: ${({ $mode, $splitRatio }) =>
        $mode === 'split'
            ? `minmax(0, ${$splitRatio}fr) 12px minmax(0, ${1 - $splitRatio}fr)`
            : 'minmax(0, 1fr)'};
    gap: 16px;
    flex: 1 1 auto;
    min-height: 0;
`;

// Drag handle between the image and OCR panes (mirrors the workspace's rail
// Resizer: thin grab line, pointer drag + arrow-key resize).
const SplitResizer = styled.div<{ $dragging: boolean }>`
    width: 12px;
    height: 100%;
    cursor: col-resize;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
        content: '';
        width: 2px;
        height: 32px;
        border-radius: 999px;
        background: ${({ $dragging }) => ($dragging ? '#999' : '#ddd')};
        transition: background 0.15s ease;
    }

    &:hover::before {
        background: #999;
    }

    &:focus-visible::before {
        background: #2563eb;
    }
`;

const ImagePane = styled.div`
    position: relative;
    border-radius: 8px;
    background: #f5f3ef;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    /* Fill the extra vertical space of the content area so the image can be
       centered within it. A floor keeps it usable when the viewport is short. */
    flex: 1 1 auto;
    min-height: 360px;
    /* Stay bounded by the content area: never let a wide zoomed image push the
       pane wider than its column. The image overflow scrolls inside ImageArea,
       so the pane (and its anchored zoom control) keep a fixed size/position. */
    min-width: 0;
    overflow: hidden;
`;

// Min legible base width for a page image (~ a typed 8.5×11 page on screen).
// At zoom 1.0 the page is at least this wide even on a narrow screen, so the
// container scrolls to reveal it instead of shrinking it to an illegible size.
const BASE_PAGE_WIDTH = 680;

const ImageArea = styled.div`
    flex: 1 1 auto;
    /* Both min-* = 0 so this fills its allotted space but can also shrink BELOW
       the image's intrinsic size — that's what lets the oversized/zoomed image
       scroll INSIDE here instead of stretching the pane outward. */
    min-height: 0;
    min-width: 0;
    /* Fill the pane's extra space and center the image within it. The image
       keeps its (zoomed) natural size — never shrunk to fit — so when it's
       larger than this area we scroll. Centering uses the child's margin:auto
       (not justify/align-content:center), which stays scroll-safe: the top/left
       edges remain reachable when the image overflows. */
    display: flex;
    overflow: auto;
`;

const PageImg = styled.img<{ $zoom: number }>`
    display: block;
    /* Center in both axes when it fits; edges stay reachable when it overflows. */
    margin: auto;
    /* Don't let the flex container shrink it — keep the zoomed size and scroll. */
    flex: 0 0 auto;
    width: ${({ $zoom }) => Math.round(BASE_PAGE_WIDTH * $zoom)}px;
    max-width: none;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const ZoomControl = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ZoomButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #334155;
    cursor: pointer;

    &:hover:not(:disabled) { background: #f1f5f9; }
    &:disabled { opacity: 0.4; cursor: default; }
`;

const ZoomLevel = styled.button`
    min-width: 48px;
    height: 26px;
    padding: 0 6px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #334155;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    cursor: pointer;

    &:hover { background: #f1f5f9; }
`;

const Placeholder = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #64748b;
`;

const OcrPane = styled.div`
    border-radius: 4px;
    background: #fff;
    padding: 32px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow-y: auto;
    min-height: 300px;
`;

// Rendered-markdown container for the OCR text (PaddleOCR-VL emits markdown).
// Block-level (not <pre>) so headings/paragraphs/lists/tables lay out properly;
// the markdown renderer preserves intra-line structure, so we don't need
// pre-wrap. Styles target the elements `marked` produces.
const OcrText = styled.div`
    font-size: 14px;
    line-height: 1.6;
    color: #0f172a;
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    word-break: break-word;

    > :first-child { margin-top: 0; }
    > :last-child { margin-bottom: 0; }

    h1, h2, h3, h4, h5, h6 {
        line-height: 1.3;
        margin: 1.2em 0 0.5em;
        font-weight: 600;
    }
    h1 { font-size: 1.4em; }
    h2 { font-size: 1.25em; }
    h3 { font-size: 1.1em; }

    p { margin: 0 0 0.8em; }

    ul, ol { margin: 0 0 0.8em; padding-left: 1.5em; }
    li { margin: 0.2em 0; }

    blockquote {
        margin: 0 0 0.8em;
        padding-left: 12px;
        border-left: 3px solid #e2e8f0;
        color: #475569;
    }

    code {
        background: #f1f5f9;
        border-radius: 3px;
        padding: 0 4px;
        font-size: 0.9em;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }
    pre {
        background: #f8fafc;
        border-radius: 6px;
        padding: 12px;
        overflow-x: auto;
        margin: 0 0 0.8em;
    }
    pre code { background: none; padding: 0; }

    table {
        border-collapse: collapse;
        margin: 0 0 0.8em;
        font-size: 0.95em;
    }
    th, td {
        border: 1px solid #e2e8f0;
        padding: 4px 8px;
        text-align: left;
    }

    img { max-width: 100%; height: auto; }

    hr { border: none; border-top: 1px solid #e2e8f0; margin: 1em 0; }

    mark {
        background: #fde68a;
        color: inherit;
        border-radius: 2px;
        padding: 0 2px;
    }
`;

const DuplicatesPane = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow-y: auto;
    height: 100%;
`;

const DuplicatePairCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
`;

const DuplicatePairHeader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 32px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
`;

const DuplicateMetric = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MetricLabel = styled.span`
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
`;

const MetricValue = styled.span`
    font-size: 16px;
    color: #0f172a;
    font-weight: 700;
`;

const DuplicatePairContent = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    flex: 1;
    min-height: 0;
`;

const DuplicateSide = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
`;

const DuplicateThumbnail = styled.div`
    flex: 1;
    border-radius: 4px;
    border: 1px solid #e8e8e8;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    min-height: 0;
`;

const DuplicateThumbImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const DuplicateInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
`;

const DuplicateTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DuplicateSubtitle = styled.div`
    font-size: 13px;
    color: #64748b;
`;

const DuplicateNavButton = styled.button`
    padding: 8px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: white;
    color: #2563eb;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.2s;

    &:hover {
        background: #eff6ff;
        border-color: #2563eb;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px;
    color: #64748b;
`;

export const DocumentCanvasPanel: React.FC<DocumentCanvasPanelProps> = ({
    page,
    pages,
    activePageId,
    scrollToken,
    scrollResetKey,
    onViewportPageChange,
    zoom: controlledZoom,
    ocrText,
    searchQuery,
    mode,
    duplicateData,
    onNavigateToDuplicate,
    getThumbnailUrl,
    previousDocument,
    nextDocument,
    onNavigateToDocument,
}) => {
    // Page-image zoom. 1.0 = the base legible width (BASE_PAGE_WIDTH); the user
    // scrolls the container to see a page taller/wider than the viewport.
    // Controlled by the toolbar in the inspect view; internal state (with the
    // floating control) only for legacy single-page usage.
    const [internalZoom, setInternalZoom] = React.useState(1);
    const zoom = controlledZoom ?? internalZoom;
    const zoomControlled = controlledZoom != null;
    const ZOOM_MIN = 0.5;
    const ZOOM_MAX = 4;
    const ZOOM_STEP = 0.1;
    const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
    const zoomBy = (delta: number) =>
        setInternalZoom(z => clampZoom(Math.round((z + delta) * 100) / 100));

    // Fetch only the resolution the current zoom actually displays: the URL's
    // width bucket follows zoom × devicePixelRatio (no-op for legacy URLs).
    // Buckets are immutable-cached, so zooming back down never re-downloads.
    const pageImageSrc = React.useMemo(() => {
        if (!page?.imageUrl) {
            return undefined;
        }
        return withPageImageWidth(
            page.imageUrl,
            selectPageImageWidth(BASE_PAGE_WIDTH * zoom),
        );
    }, [page?.imageUrl, zoom]);

    const continuous = !!pages && pages.length > 0;

    // The OCR pane keeps its own scroll position; snap it back to the top
    // whenever the page whose text it shows changes (viewport scroll in
    // continuous mode, explicit nav, or a document switch).
    const ocrPaneRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        if (ocrPaneRef.current) {
            ocrPaneRef.current.scrollTop = 0;
        }
    }, [page?.id]);

    // Split-view divider position: fraction of the content width given to the
    // image pane. Dragged via the SplitResizer; resets per mount (parity with
    // the workspace rail resizer).
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const [splitRatio, setSplitRatio] = React.useState(0.5);
    const [isSplitResizing, setIsSplitResizing] = React.useState(false);
    const clampRatio = (r: number) => Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, r));

    const handleSplitPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        const containerWidth = contentRef.current?.getBoundingClientRect().width ?? 0;
        if (!containerWidth) {
            return;
        }
        const startX = event.clientX;
        const startRatio = splitRatio;

        const handlePointerMove = (moveEvent: PointerEvent) => {
            const delta = (moveEvent.clientX - startX) / containerWidth;
            setSplitRatio(clampRatio(startRatio + delta));
        };

        const handlePointerUp = () => {
            setIsSplitResizing(false);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };

        setIsSplitResizing(true);
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    };

    const handleSplitKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = event.shiftKey ? 0.1 : 0.04;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            setSplitRatio(r => clampRatio(r - step));
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            setSplitRatio(r => clampRatio(r + step));
        }
    };

    const renderImagePane = () => (
        <ImagePane>
            {/* Floating control only when zoom is uncontrolled (legacy
                single-page usage). The inspect toolbar owns zoom otherwise —
                nothing floats over the page. */}
            {!zoomControlled && (continuous || page?.imageUrl) && (
                <ZoomControl role="group" aria-label="Zoom page image">
                    <ZoomButton
                        type="button"
                        onClick={() => zoomBy(-ZOOM_STEP)}
                        disabled={zoom <= ZOOM_MIN}
                        aria-label="Zoom out"
                        title="Zoom out"
                    >
                        <ZoomOut size={15} />
                    </ZoomButton>
                    <ZoomLevel
                        type="button"
                        onClick={() => setInternalZoom(1)}
                        aria-label="Reset zoom"
                        title="Reset zoom"
                    >
                        {Math.round(zoom * 100)}%
                    </ZoomLevel>
                    <ZoomButton
                        type="button"
                        onClick={() => zoomBy(ZOOM_STEP)}
                        disabled={zoom >= ZOOM_MAX}
                        aria-label="Zoom in"
                        title="Zoom in"
                    >
                        <ZoomIn size={15} />
                    </ZoomButton>
                </ZoomControl>
            )}
            {continuous ? (
                <ContinuousPageStack
                    pages={pages!}
                    zoom={zoom}
                    baseWidth={BASE_PAGE_WIDTH}
                    activePageId={activePageId ?? null}
                    scrollToken={scrollToken ?? 0}
                    scrollResetKey={scrollResetKey ?? null}
                    onCurrentPageChange={onViewportPageChange}
                    previousDocument={previousDocument}
                    nextDocument={nextDocument}
                    onNavigateToDocument={onNavigateToDocument}
                />
            ) : (
                <ImageArea>
                    {pageImageSrc ? (
                        <PageImg
                            src={pageImageSrc}
                            alt={`Document page ${page?.pageNumber}`}
                            $zoom={zoom}
                        />
                    ) : (
                        <Placeholder>
                            <ImageIcon size={24} />
                            <span>No image available</span>
                        </Placeholder>
                    )}
                </ImageArea>
            )}
            {/* Current-page metadata chips sit BELOW the image area so nothing
                labels the top of the page (compact-chrome constraint). */}
            <PageMetaStrip page={page} />
        </ImagePane>
    );

    const renderOcrPane = () => (
        <OcrPane ref={ocrPaneRef} data-testid="ocr-pane">
            {ocrText ? (
                // PaddleOCR-VL output is markdown: render it, with best-effort
                // search highlighting applied after rendering.
                <OcrText
                    dangerouslySetInnerHTML={{
                        __html: renderMarkdownWithHighlight(ocrText, searchQuery),
                    }}
                />
            ) : (
                <Placeholder>
                    <FileText size={20} />
                    <span>No OCR text available</span>
                </Placeholder>
            )}
        </OcrPane>
    );

    const renderDuplicatesPane = () => {
        if (!duplicateData || !page) {
            return (
                <DuplicatesPane>
                    <EmptyState>
                        <Copy size={32} />
                        <span>No duplicate data available</span>
                    </EmptyState>
                </DuplicatesPane>
            );
        }

        // Filter duplicates relevant to current page
        const relevantDuplicates = duplicateData.filter(
            pair => pair.left.page_id === page.id || pair.right.page_id === page.id
        );

        if (relevantDuplicates.length === 0) {
            return (
                <DuplicatesPane>
                    <EmptyState>
                        <Copy size={32} />
                        <span>No duplicates found for this page</span>
                    </EmptyState>
                </DuplicatesPane>
            );
        }

        return (
            <DuplicatesPane>
                {relevantDuplicates.map((pair, idx) => {
                    // Determine which side is the current page and which is the duplicate
                    const isLeftCurrent = pair.left.page_id === page.id;
                    const currentSide = isLeftCurrent ? pair.left : pair.right;
                    const otherSide = isLeftCurrent ? pair.right : pair.left;

                    const similarity = (pair.cosine_similarity ?? 0) * 100;

                    return (
                        <DuplicatePairCard key={`${pair.left.page_id}-${pair.right.page_id}-${idx}`}>
                            <DuplicatePairHeader>
                                <DuplicateMetric>
                                    <MetricLabel>Similarity</MetricLabel>
                                    <MetricValue>{similarity.toFixed(1)}%</MetricValue>
                                </DuplicateMetric>
                            </DuplicatePairHeader>

                            <DuplicatePairContent>
                                {/* Current Page Side */}
                                <DuplicateSide>
                                    <DuplicateThumbnail>
                                        {getThumbnailUrl && getThumbnailUrl(currentSide.page_id, currentSide.page_index ?? undefined) ? (
                                            <DuplicateThumbImg
                                                src={getThumbnailUrl(currentSide.page_id, currentSide.page_index ?? undefined) || ''}
                                                alt={`Page ${currentSide.page_number || ''}`}
                                            />
                                        ) : (
                                            <Placeholder>
                                                <ImageIcon size={20} />
                                            </Placeholder>
                                        )}
                                    </DuplicateThumbnail>
                                    <DuplicateInfo>
                                        <DuplicateTitle>
                                            {currentSide.doc_title || currentSide.doc_id}
                                        </DuplicateTitle>
                                        <DuplicateSubtitle>
                                            Page {currentSide.page_number || currentSide.page_name || '?'} (Current)
                                        </DuplicateSubtitle>
                                    </DuplicateInfo>
                                </DuplicateSide>

                                {/* Duplicate Page Side */}
                                <DuplicateSide>
                                    <DuplicateThumbnail>
                                        {getThumbnailUrl && getThumbnailUrl(otherSide.page_id, otherSide.page_index ?? undefined) ? (
                                            <DuplicateThumbImg
                                                src={getThumbnailUrl(otherSide.page_id, otherSide.page_index ?? undefined) || ''}
                                                alt={`Page ${otherSide.page_number || ''}`}
                                            />
                                        ) : (
                                            <Placeholder>
                                                <ImageIcon size={20} />
                                            </Placeholder>
                                        )}
                                    </DuplicateThumbnail>
                                    <DuplicateInfo>
                                        <DuplicateTitle>
                                            {otherSide.doc_title || otherSide.doc_id}
                                        </DuplicateTitle>
                                        <DuplicateSubtitle>
                                            Page {otherSide.page_number || otherSide.page_name || '?'}
                                        </DuplicateSubtitle>
                                    </DuplicateInfo>
                                    {onNavigateToDuplicate && (
                                        <DuplicateNavButton
                                            onClick={() =>
                                                onNavigateToDuplicate(
                                                    otherSide.doc_id,
                                                    otherSide.page_number || undefined
                                                )
                                            }
                                        >
                                            <ExternalLink size={12} />
                                            View Document
                                        </DuplicateNavButton>
                                    )}
                                </DuplicateSide>
                            </DuplicatePairContent>
                        </DuplicatePairCard>
                    );
                })}
            </DuplicatesPane>
        );
    };

    return (
        <Panel>
            {/* The "Page N" heading moved into the toolbar's page indicator —
                the canvas starts directly with the page content. */}
            <Content $mode={mode} $splitRatio={splitRatio} ref={contentRef}>
                {(mode === 'image' || mode === 'split') && renderImagePane()}
                {mode === 'split' && (
                    <SplitResizer
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize image and OCR panes"
                        aria-valuemin={Math.round(SPLIT_MIN * 100)}
                        aria-valuemax={Math.round(SPLIT_MAX * 100)}
                        aria-valuenow={Math.round(splitRatio * 100)}
                        tabIndex={0}
                        onPointerDown={handleSplitPointerDown}
                        onKeyDown={handleSplitKeyDown}
                        $dragging={isSplitResizing}
                    />
                )}
                {(mode === 'ocr' || mode === 'split') && renderOcrPane()}
                {mode === 'duplicates' && renderDuplicatesPane()}
            </Content>
        </Panel>
    );
};
